import type { FormField, FormSection } from '../../types';

// Mirror of buildStudentUpdatesFromResponses' supported paths in
// src/forms/utils/maps-to-walker.ts. Keep them aligned — anything not in
// this list won't be written back. The full list is small enough to inline
// rather than computing it from the seeded form.
const STUDENT_PATHS = [
  'student.address',
  'student.allergies.detail',
  'student.allergies.hasAny',
  'student.authorisedPickup',
  'student.consents.antiFebrile',
  'student.consents.childProtection',
  'student.consents.medicalTreatment',
  'student.consents.sunCream',
  'student.dateOfBirth',
  'student.emergencyContacts',
  'student.firstName',
  'student.gp.address',
  'student.gp.name',
  'student.gp.phone',
  'student.healthInsurance.policy',
  'student.healthInsurance.provider',
  'student.immunisations.fileKey',
  'student.immunisations.recordOnFile',
  'student.lastName',
  'student.medicalHistory',
  'student.specialNeeds',
];

interface Props {
  field: FormField;
  sections: FormSection[];
  onChange: (patch: Partial<FormField>) => void;
}

/**
 * Per-field property editor surfaced when an admin expands a field row in
 * the FormBuilder. Mirrors the backend's stable key + mapsTo writeback +
 * requiredWhen rule surface. Locked fields have key+mapsTo disabled;
 * requiredLocked fields have the Required toggle disabled (handled in the
 * FormBuilder row itself, kept duplicated here for the same reason).
 */
export function FieldPropertyPanel({ field, sections, onChange }: Props) {
  const locked = field.locked === true;
  const requiredLocked = field.requiredLocked === true;
  const allFields = sections.flatMap((s) => s.fields).filter((f) => f.key !== field.key);
  const rule = field.requiredWhen;
  const operator: 'none' | 'equals' | 'notEmpty' = !rule
    ? 'none'
    : 'notEmpty' in rule
    ? 'notEmpty'
    : 'equals';

  return (
    <div
      data-testid={`field-property-panel-${field.id}`}
      className="mt-4 space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4"
    >
      <div>
        <label className="block text-xs font-medium text-gray-600">Key</label>
        <input
          type="text"
          value={field.key}
          disabled={locked}
          data-testid={`field-property-key-${field.id}`}
          onChange={(e) => onChange({ key: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          id={`field-property-required-${field.id}`}
          type="checkbox"
          checked={field.required}
          disabled={requiredLocked}
          data-testid={`field-property-required-${field.id}`}
          onChange={(e) => onChange({ required: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:bg-gray-100"
        />
        <label
          htmlFor={`field-property-required-${field.id}`}
          className="text-xs font-medium text-gray-600"
        >
          Required{requiredLocked && ' (locked on)'}
        </label>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600">Maps to student field</label>
        <select
          value={field.mapsTo ?? ''}
          disabled={locked}
          data-testid={`field-property-mapsto-${field.id}`}
          onChange={(e) => onChange({ mapsTo: e.target.value || undefined })}
          className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
        >
          <option value="">(no writeback)</option>
          {STUDENT_PATHS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="space-y-2 rounded-md border border-gray-200 bg-white p-3">
        <legend className="px-1 text-xs font-medium text-gray-600">Conditional requirement</legend>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Operator</label>
          <select
            value={operator}
            data-testid={`field-property-when-op-${field.id}`}
            onChange={(e) => {
              const op = e.target.value as 'none' | 'equals' | 'notEmpty';
              if (op === 'none') onChange({ requiredWhen: undefined });
              else if (op === 'notEmpty')
                onChange({ requiredWhen: { field: rule?.field ?? '', notEmpty: true } });
              else onChange({ requiredWhen: { field: rule?.field ?? '', equals: '' } });
            }}
            className="rounded-md border-gray-300 text-sm shadow-sm"
          >
            <option value="none">always (no rule)</option>
            <option value="equals">required when sibling equals…</option>
            <option value="notEmpty">required when sibling is non-empty</option>
          </select>
        </div>
        {operator !== 'none' && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Sibling field</label>
            <select
              value={rule?.field ?? ''}
              data-testid={`field-property-when-field-${field.id}`}
              onChange={(e) =>
                onChange({
                  requiredWhen:
                    operator === 'notEmpty'
                      ? { field: e.target.value, notEmpty: true }
                      : {
                          field: e.target.value,
                          equals: rule && 'equals' in rule ? rule.equals : '',
                        },
                })
              }
              className="rounded-md border-gray-300 text-sm shadow-sm"
            >
              <option value="">— pick —</option>
              {allFields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label} ({f.key})
                </option>
              ))}
            </select>
          </div>
        )}
        {operator === 'equals' && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Equals value</label>
            <input
              type="text"
              value={String(rule && 'equals' in rule ? rule.equals : '')}
              data-testid={`field-property-when-equals-${field.id}`}
              onChange={(e) =>
                onChange({
                  requiredWhen: { field: rule?.field ?? '', equals: e.target.value },
                })
              }
              className="rounded-md border-gray-300 text-sm shadow-sm"
            />
          </div>
        )}
      </fieldset>
    </div>
  );
}
